import React, { useState, useEffect } from 'react';
import { ArrowLeft, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { rtiOfficersService } from '../../services/api/rtiOfficersService';
import type { RTIOfficer } from '../../types/rti-officers.types';

const RTIOfficersView: React.FC = () => {
  const navigate = useNavigate();
  const [officers, setOfficers] = useState<RTIOfficer[]>([]);
  const [groupedOfficers, setGroupedOfficers] = useState<Record<number, RTIOfficer[]>>({});
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOfficers: 0,
    totalSections: 0,
    appellateAuthorities: 0,
    pios: 0,
    apios: 0,
  });

  const sectionNames: Record<number, string> = {
    1: 'Administration, Budget, Accounts and loans',
    2: 'CSS and State Projects, PMGSY & Buildings',
    3: 'MRR',
    4: 'Vig & Quality Control',
    5: 'Designs, MIS & Planning and MGNREGS',
    6: 'SQC',
  };

  const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [officersResponse, statsResponse] = await Promise.all([
        rtiOfficersService.getAll(),
        rtiOfficersService.getStatistics(),
      ]);

      const data = officersResponse.data || [];
      setOfficers(data);
      setStats(statsResponse.data);

      // Group by section
      const grouped: Record<number, RTIOfficer[]> = {};
      data.forEach((officer) => {
        if (!grouped[officer.section_number]) {
          grouped[officer.section_number] = [];
        }
        grouped[officer.section_number].push(officer);
      });

      setGroupedOfficers(grouped);
    } catch (error) {
      toast.error('Failed to fetch RTI officers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPostBadgeColor = (post: string): string => {
    if (post === 'Appellate Authority') return 'bg-purple-100 text-purple-800';
    if (post.startsWith('PIO')) return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading RTI officers...</p>
        </div>
      </div>
    );
  }

  const sections = Object.keys(groupedOfficers)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">For RTI</h1>
          <p className="mt-1 text-sm text-gray-600">
            Right to Information Officers
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          <ArrowLeft size={18} className="mr-2" />
          Back
        </button>
      </div>

      {/* Department Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h2 className="text-base font-semibold text-gray-900 leading-relaxed">
          NAME & ADDRESS OF THE DEPARTMENT: O/o. ENGINEER-IN-CHIEF, PANCHAYAT RAJ ENGINEERING DEPARTMENT,
          S.R.T.G.N. BHAVAN, ERRUM-MANZIL COLONY, TELANGANA STATE, HYDERABAD.
        </h2>
      </div>

      {/* Main Content */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  colSpan={2}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20"
                >
                  Sl.NO.
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56">
                  Name and Designation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Post
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subjects allotted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                  Address
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sections.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No RTI officers data available
                  </td>
                </tr>
              ) : (
                sections.map((sectionNum) => {
                  const sectionOfficers = groupedOfficers[sectionNum];
                  return (
                    <React.Fragment key={sectionNum}>
                      {sectionOfficers.map((officer, index) => (
                        <tr key={officer.id} className="hover:bg-gray-50 transition-colors">
                          {/* Section Number (Roman Numeral) */}
                          {index === 0 && (
                            <td
                              rowSpan={sectionOfficers.length}
                              className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 align-top border-r border-gray-200"
                            >
                              {romanNumerals[sectionNum - 1]}
                            </td>
                          )}

                          {/* Serial Number */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {officer.serial_number}
                          </td>

                          {/* Name and Designation */}
                          <td className="px-6 py-4 text-sm">
                            <div className="font-medium text-gray-900">{officer.name}</div>
                            <div className="text-gray-600">{officer.designation}</div>
                          </td>

                          {/* Post */}
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`inline-flex px-2.5 py-1 rounded-md text-xs font-semibold ${getPostBadgeColor(
                                officer.post
                              )}`}
                            >
                              {officer.post}
                            </span>
                          </td>

                          {/* Subjects Allotted */}
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {officer.subjects_allotted}
                          </td>

                          {/* Address */}
                          <td className="px-6 py-4 text-sm">
                            <div className="text-gray-900">{officer.address}</div>
                            <div className="flex items-center mt-2 text-gray-600">
                              <Phone size={14} className="mr-2 flex-shrink-0" />
                              <span className="font-medium">{officer.mobile_number}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-gray-600">Total Officers</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{stats.totalOfficers}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-gray-600">Sections</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{stats.totalSections}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-gray-600">Appellate Authorities</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">
            {stats.appellateAuthorities}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="text-sm font-medium text-gray-600">PIOs & APIOs</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">
            {stats.pios + stats.apios}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RTIOfficersView;